import React, {useMemo, useState} from "react";
import {Grid} from "@mui/material";
import {Dropdown} from "../../../../designSystem/components/Dropdown";
import {BaseInput} from "../../../../designSystem/components/BaseInput";
import {AddressInput} from "../../../../designSystem/components/AddressInput";
import VaccineButtons from "../../../../designSystem/components/VaccineButtons/VaccineButtons";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {OptionButton} from "../../../../designSystem/components/OptionButton";
import {Typography} from "../../../../designSystem/components/Typography";

import {MedicalOrganizationVaccine} from "../../../../types/MedicalOrganizationVaccine";
import {ImageUploaderCropper} from "../../../../components/ImageUploader/ImageUploaderCropper";
import {formatAddress} from "../../model/address";
import {ORGANIZATION_TYPES} from "../../../../utils/constants";

interface MedicalOrganizationFormProps {
  mode: 'edit' | 'add';
  medicalOrganizationVaccine?: MedicalOrganizationVaccine;
  onChangeMedicalOrganization: (medicalOrganization: MedicalOrganizationVaccine) => void;
  onClickSave: () => void;
}

const MedicalOrganizationForm = ({
  mode,
  medicalOrganizationVaccine,
  onChangeMedicalOrganization,
  onClickSave
}: MedicalOrganizationFormProps) => {

  const [uploadMode, setUploadMode] = useState<boolean>(!medicalOrganizationVaccine.coverImageUrl);

  const onChangeField = (fieldName: string, value: string | string[] | boolean) => {
    let tempMedicalOrganization;
    if (fieldName === 'address') {
      const [region, address] = formatAddress('', value as string)
      tempMedicalOrganization = {
        ...medicalOrganizationVaccine,
        region,
        address: value,
      }
    } else {
      tempMedicalOrganization = {
        ...medicalOrganizationVaccine,
        [fieldName]: value
      }
    }
    onChangeMedicalOrganization(tempMedicalOrganization);
  }

  const onChangeSelectedVaccine = (vaccine) => {
    let tempVaccines;
    if (medicalOrganizationVaccine.vaccines.includes(vaccine.name)) {
      tempVaccines = medicalOrganizationVaccine.vaccines.filter(v => v !== vaccine.name);
    } else {
      tempVaccines = [...medicalOrganizationVaccine.vaccines, vaccine.name];
    }
    onChangeField('vaccines', tempVaccines);
  }

  const [region, address] = formatAddress('', medicalOrganizationVaccine.address)

  const isValid = useMemo(() => {
    return (
      medicalOrganizationVaccine?.name
      && medicalOrganizationVaccine?.category
      && medicalOrganizationVaccine?.address
      && medicalOrganizationVaccine?.longitude
      && medicalOrganizationVaccine?.latitude
      && medicalOrganizationVaccine?.telephone
    )
  }, [medicalOrganizationVaccine])

  return (
    <div>
      <div className="flex flex-col p-8 gap-4">
        <Typography variant="title">
          {mode === 'edit' ? '編輯機構資訊' : '新增機構'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <div className="page-title mb-2">
              名稱
            </div>
            <BaseInput
              onChange={e => onChangeField('name', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.name}
              placeholder="機構名稱"
            />
          </Grid>
          <Grid item xs={6}>
            <div className="page-title mb-2">
              類別
            </div>
            <Dropdown
              className={""}
              hasDiv={false}
              options={ORGANIZATION_TYPES.slice(1, ORGANIZATION_TYPES.length).map(type => type.name)}
              dataSelected={medicalOrganizationVaccine.category}
              disabled={false}
              onChange={e => onChangeField('category', e.target.value)}
              text="類別"
            />
          </Grid>
          <Grid item xs={12}>
            <AddressInput
              values={{ address, region }}
              onChange={object => {
                const joinedAddress = `屏東縣${object.region}${object.address}`;
                onChangeField('address', joinedAddress);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              封面圖片 (小於3MB)
            </div>
            {!uploadMode
              ? (
                <div className="flex flex-col gap-4">
                  <img
                    src={medicalOrganizationVaccine.coverImageUrl}
                    alt="cover image url"
                  />
                  <ActionButton
                    label="重新上傳"
                    onClick={() => setUploadMode(true)}
                    variant="outline"
                  />
                </div>
              )
              : (
                <ImageUploaderCropper
                  onUploaded={url => {
                    onChangeField('coverImageUrl', url);
                    setUploadMode(false);
                  }}
                  folder="medicalOrganizationCover"
                />
              )
            }
          </Grid>
          <Grid item xs={4}>
            <div className="page-title mb-2">
            經度
            </div>
            <BaseInput
              type="number"
              onChange={e => onChangeField('longitude', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.longitude}
              placeholder="經度"
            />
          </Grid>
          <Grid item xs={4}>
            <div className="page-title mb-2">
              緯度
            </div>
            <BaseInput
              type="number"
              onChange={e => onChangeField('latitude', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.latitude}
              placeholder="緯度"
            />
          </Grid>
          <Grid item xs={4}>
            <div className="page-title mb-2">
              電話
            </div>
            <BaseInput
              onChange={e => onChangeField('telephone', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.telephone}
              placeholder="電話"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              官方網站網址
            </div>
            <BaseInput
              onChange={e => onChangeField('officialWeb', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.officialWeb}
              placeholder="官方網站網址"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              掛號網站網址
            </div>
            <BaseInput
              onChange={e => onChangeField('appointmentWeb', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.appointmentWeb}
              placeholder="掛號網站網址"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              看診進度網站網址
            </div>
            <BaseInput
              onChange={e => onChangeField('progressWeb', e.target.value)}
              isDisabled={false}
              value={medicalOrganizationVaccine.progressWeb}
              placeholder="看診進度網站網址"
            />
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              標籤
            </div>
            <div className="flex gap-2 mb-2 flex-wrap px-2 py-2">
              <OptionButton
                isSelected={medicalOrganizationVaccine.smokingCessation}
                onClick={() => onChangeField('smokingCessation', !medicalOrganizationVaccine.smokingCessation)}
                label="戒菸"
              />
              <OptionButton
                isSelected={medicalOrganizationVaccine.icope}
                onClick={() => onChangeField('icope', !medicalOrganizationVaccine.icope)}
                label="ICOPE評估醫事單位"
              />
              <OptionButton
                isSelected={medicalOrganizationVaccine.hpaPrevention}
                onClick={() => onChangeField('hpaPrevention', !medicalOrganizationVaccine.hpaPrevention)}
                label="代謝症候群防治機構"
              />
              <OptionButton
                isSelected={medicalOrganizationVaccine.adultHealthScreening}
                onClick={() => onChangeField('adultHealthScreening', !medicalOrganizationVaccine.adultHealthScreening)}
                label="成人健檢之醫療院所"
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className="page-title mb-2">
              疫苗
            </div>
            <VaccineButtons
              selected={medicalOrganizationVaccine.vaccines}
              onChangeSelected={onChangeSelectedVaccine}
            />
          </Grid>
        </Grid>
      </div>
      <div className="flex flex-col self-start flex-1 space-y-3">
        <hr/>
        <div className={"px-4 pb-4 pt-1"}>
          <ActionButton
            label={mode === 'edit' ? "儲存變更" : "確定新增"}
            iconName={mode === 'edit' ? "fa-save" : "fa-check"}
            onClick={onClickSave}
            fullWidth
            disabled={!isValid}
          />
        </div>
      </div>
    </div>
  )
}

export default MedicalOrganizationForm
